import { Children, FC, ReactNode, useMemo } from "react";
import {
  PositionsPanelContext,
  usePositionsPanelContext,
} from "./position-panel-context";
import {
  Links,
  Positions,
  UseAssetPriceInUSD,
  UseQuotePrice,
} from "./types";
import {
  disjoinSymbols,
  getBorrowState,
  getDepositAssets,
  getHasNoPositions,
  getIsLoan,
} from "./utils";
import { DepositAsset } from "./deposit-asset";
import { match } from "ts-pattern";
import { BorrowAsset, BorrowingDisabled, NoBorrows } from "./borrow-asset";
import { LoanMeta } from "./loan-meta";
import { twMerge } from "tailwind-merge";
import { disjoin } from "@silo/utils";
import { EmptyText } from "../empty-text";

export interface PositionsPanelProps {
  positions: Positions;
  assetLogo: ReactNode;
  useAssetPriceInUSD: UseAssetPriceInUSD;
  useQuotePrice: UseQuotePrice;
  id?: string;
  links: Links;
  healthFactorSlot: ReactNode;
  borrowingPowerUsedSlot: ReactNode;
  title?: ReactNode;
  disabledCollateralDescription?: ReactNode;
  afterActionGroupSlot?: ReactNode;
  rewardsSymbol: string;
}

/**
 * @deprecated use `LendingPosition` instead
 */
export const PositionsPanel = ({
  positions,
  assetLogo,
  useAssetPriceInUSD,
  useQuotePrice,
  links,
  disabledCollateralDescription,
  afterActionGroupSlot,
  title,
  healthFactorSlot,
  borrowingPowerUsedSlot,
  rewardsSymbol,
}: PositionsPanelProps) => {
  const hasNoPositions = getHasNoPositions(positions);
  const hasPositions = !hasNoPositions;
  const depositAssets = getDepositAssets(positions);

  const positionsPanelContext: PositionsPanelContext = useMemo(
    () => ({
      positions,
      assetLogo,
      useAssetPriceInUSD,
      useQuotePrice,
      links,
      disabledCollateralDescription,
      afterActionGroupSlot,
    }),
    [
      positions,
      assetLogo,
      useAssetPriceInUSD,
      useQuotePrice,
      links,
      disabledCollateralDescription,
      afterActionGroupSlot,
    ]
  );

  const borrowState = getBorrowState(positions);
  const isLoan = getIsLoan(positions);
  const showLoanMeta = !!title || isLoan;

  return (
    <PositionsPanelContext.Provider value={positionsPanelContext}>
      <div className="@container">
        <div className="block @screen-sm:hidden">
          <Panel>
            {showLoanMeta && (
              <LoanMeta
                title={title}
                healthFactorSlot={healthFactorSlot}
                borrowingPowerUsedSlot={borrowingPowerUsedSlot}
              />
            )}

            {hasNoPositions && <NoPositions />}

            {depositAssets.map((asset) => (
              <DepositAsset
                asset={asset}
                divider
                rewardsSymbol={rewardsSymbol}
              />
            ))}

            {hasPositions &&
              match(borrowState)
                .with({ _tag: "disabled" }, () => <BorrowingDisabled />)
                .with({ _tag: "noBorrows" }, () => <NoBorrows />)
                .with({ _tag: "openBorrows" }, ({ assets }) =>
                  assets.map((asset) => (
                    <BorrowAsset
                      asset={asset}
                      divider
                      rewardsSymbol={rewardsSymbol}
                    />
                  ))
                )
                .exhaustive()}
          </Panel>
        </div>

        <div className="hidden @screen-sm:block">
          <Panel>
            <div className="flex flex-col gap-10">
              {showLoanMeta && (
                <LoanMeta
                  title={title}
                  healthFactorSlot={healthFactorSlot}
                  borrowingPowerUsedSlot={borrowingPowerUsedSlot}
                />
              )}

              {hasNoPositions && <NoPositions />}

              {hasPositions && (
                <div className="flex gap-8">
                  <div
                    className="flex flex-col flex-1 gap-14"
                    data-cy="deposit-position"
                  >
                    {depositAssets.map((asset, i) => (
                      <>
                        {i > 0 && <div className="h-px bg-shale-700" />}

                        <DepositAsset
                          asset={asset}
                          rewardsSymbol={rewardsSymbol}
                        />
                      </>
                    ))}
                  </div>

                  <div className="w-px bg-shale-700" />

                  <div
                    className="flex flex-col flex-1 gap-14"
                    data-cy="borrow-position"
                  >
                    {match(borrowState)
                      .with({ _tag: "disabled" }, () => <BorrowingDisabled />)
                      .with({ _tag: "noBorrows" }, () => <NoBorrows />)
                      .with({ _tag: "openBorrows" }, ({ assets }) =>
                        assets.map((asset, i) => (
                          <>
                            {i > 0 && <div className="h-px bg-shale-700" />}

                            <BorrowAsset
                              asset={asset}
                              rewardsSymbol={rewardsSymbol}
                            />
                          </>
                        ))
                      )
                      .exhaustive()}
                  </div>
                </div>
              )}
            </div>
          </Panel>
        </div>
      </div>
    </PositionsPanelContext.Provider>
  );
};

const NoPositions: FC = () => {
  const { positions } = usePositionsPanelContext();

  return (
    <div data-cy="positions-empty" className="flex flex-col gap-2.5">
      <h2 className="font-medium text-white">
        You don{"'"}t have any open positions.
      </h2>
      <p className="text-shale-200">
        Deposit some {disjoinSymbols(positions.assets)} to get started.
      </p>
    </div>
  );
};

interface PanelProps {
  className?: string;
  children?: ReactNode;
}

const Panel = ({ className, children }: PanelProps): JSX.Element => {
  const array = Children.toArray(children);

  return (
    <div className={twMerge("flex flex-col gap-0.5", className)}>
      {array.map((child) => (
        <div className="p-5 pb-10 bg-shale-800 first:rounded-t-lg last:rounded-b-lg">
          {child}
        </div>
      ))}
    </div>
  );
};

export const PulsePositionsPanel: FC = () => (
  <Panel className="animate-pulse">
    <div className=" flex flex-col gap-2.5">
      <div className="rounded-lg w-fit bg-shale-700">
        <EmptyText>You don{"'"}t have any open positions.</EmptyText>
      </div>

      <div className="rounded-lg w-fit bg-shale-700">
        {/* not visible, so a placeholder symbol can be used */}
        <EmptyText>
          Deposit some {disjoin(["ABC", "ABC", "ABC"])} to get started.
        </EmptyText>
      </div>
    </div>
  </Panel>
);
